import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Grid, TextField, FormControl, InputLabel, Select, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, Checkbox, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Divider,
  Stack, Tooltip, Slide, CircularProgress, Paper, TableContainer,
  Skeleton, InputAdornment
} from '@mui/material';
import {
  Add, Edit, Delete, Category as CategoryIcon, Save, Close,
  Inventory2, VisibilityOff, FolderOpen, Search as SearchIcon
} from '@mui/icons-material';
import {
  motion, AnimatePresence, useMotionValue, useTransform, animate
} from 'framer-motion';
import { toast } from 'react-toastify';
import api from '../../../../services/api';

const TransitionUp = React.forwardRef(function TransitionUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// opções padrão para unidade de medida
const MEDIDAS = [
  { value: 'und', label: 'Und' },
  { value: 'kg', label: 'Kg' },
  { value: 'g', label: 'g' },
  { value: 'l', label: 'L' },
  { value: 'ml', label: 'ml' },
  { value: 'pacote', label: 'Pacote' },
  { value: 'caixa', label: 'Caixa' },
  { value: 'garrafa', label: 'Garrafa' },
  { value: 'saco', label: 'Saco' },
  { value: 'bandeja', label: 'Bandeja' },
];

// ========== helpers ==========
const formatBRL = (n) => {
  if (n === null || n === undefined || n === '' || Number.isNaN(Number(n))) return '–';
  try {
    return Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  } catch {
    return `R$ ${Number(n).toFixed(2)}`;
  }
};

// contador animado para KPIs
const AnimatedCounter = ({ value }) => {
  const motionVal = useMotionValue(0);
  const rounded = useTransform(motionVal, (latest) => Math.round(latest).toLocaleString('pt-BR'));
  const prev = useRef(0);

  useEffect(() => {
    const controls = animate(motionVal, value ?? 0, { duration: 0.6, ease: 'easeOut' });
    prev.current = value ?? 0;
    return controls.stop;
  }, [value, motionVal]);

  return <motion.span>{rounded}</motion.span>;
};

// cartão de KPI
const KpiCard = ({ icon: IconCmp, label, value }) => (
  <Paper
    component={motion.div}
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.25 }}
    elevation={0}
    sx={{
      p: 2,
      borderRadius: 3,
      backdropFilter: 'blur(8px)',
      background: 'linear-gradient(180deg, rgba(255,255,255,0.9), rgba(255,255,255,0.6))',
      border: '1px solid',
      borderColor: 'divider'
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box
        sx={{
          p: 1,
          borderRadius: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'inline-flex'
        }}
      >
        <IconCmp fontSize="small" />
      </Box>
      <Box>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
          {label}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          <AnimatedCounter value={value ?? 0} />
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const Insumos = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoriaId, setCategoriaId] = useState('');
  const [categories, setCategories] = useState([]);

  const [page, setPage] = useState(1);
  const perPage = 10;
  const [total, setTotal] = useState(0);
  const [kpis, setKpis] = useState({ total: 0, ocultados: 0, categorias: 0 });

  // --- Insumo Modal
  const [openModal, setOpenModal] = useState(false);
  const [savingInsumo, setSavingInsumo] = useState(false);
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState({
    nome: '',
    unidade_medida: 'und',
    custo_inicial: '',
    estoque_minimo: 0,
    categoria_id: '',
    especificacao: '',
    oculto_cmv: false,
  });

  // --- Categoria Modal
  const [catModalOpen, setCatModalOpen] = useState(false);
  const [catLoading, setCatLoading] = useState(false);
  const [catNewName, setCatNewName] = useState('');
  const [editingCatId, setEditingCatId] = useState(null);
  const [editingCatName, setEditingCatName] = useState('');

  const totalPages = Math.ceil(total / perPage) || 1;

  // debounce da busca
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  // ===================== DATA =====================
  const fetchInsumos = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/confereai/insumos', {
        params: {
          q: debouncedSearch || undefined,
          categoria_id: categoriaId || undefined,
          page,
          per_page: perPage,
        },
      });
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setRows(list);
      const totalItems = data.total ?? data.meta?.total ?? list.length;
      const hidden = data.ocultados ?? list.filter((i) => i.oculto_cmv).length;
      setTotal(totalItems);
      setKpis((k) => ({ ...k, total: totalItems, ocultados: hidden }));
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar insumos');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, categoriaId, page, perPage]);

  const fetchCategorias = useCallback(async () => {
    try {
      const { data } = await api.get('/confereai/categorias', { params: { page: 1, per_page: 100 } });
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setCategories(list);
      setKpis((k) => ({ ...k, categorias: list.length }));
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar categorias');
    }
  }, []);

  useEffect(() => { fetchCategorias(); }, [fetchCategorias]);
  useEffect(() => { fetchInsumos(); }, [fetchInsumos]);

  // ===================== INSUMO MODAL =====================
  const handleOpenModal = (insumo = null) => {
    setCurrent(insumo);
    setForm({
      nome: insumo?.nome || '',
      unidade_medida: insumo?.unidade_medida || 'und',
      custo_inicial: insumo?.custo_inicial ?? '',
      estoque_minimo: insumo?.estoque_minimo ?? 0,
      categoria_id: insumo?.categoria_id || '',
      especificacao: insumo?.especificacao || '',
      oculto_cmv: !!insumo?.oculto_cmv,
    });
    setOpenModal(true);
  };

  const handleSave = async () => {
    try {
      if (!form.nome?.trim()) return toast.error('Informe o nome');

      const custo = Number(String(form.custo_inicial).replace(',', '.'));
      if (Number.isNaN(custo)) return toast.error('Informe o custo inicial');
      if (custo < 0) return toast.error('Custo inicial não pode ser negativo');

      const estoqueMin = Number(String(form.estoque_minimo).replace(',', '.'));
      if (Number.isNaN(estoqueMin) || estoqueMin < 0) return toast.error('Estoque mínimo inválido');

      const payload = {
        nome: form.nome,
        unidade_medida: form.unidade_medida || 'und',
        custo_inicial: custo,
        estoque_minimo: estoqueMin,
        categoria_id: form.categoria_id || null,
        especificacao: form.especificacao,
        oculto_cmv: form.oculto_cmv,
      };

      setSavingInsumo(true);
      if (current?.id) {
        await api.put(`/confereai/insumos/${current.id}`, payload);
        toast.success('Insumo atualizado');
      } else {
        await api.post('/confereai/insumos', payload);
        toast.success('Insumo criado');
      }
      setOpenModal(false);
      fetchInsumos();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao salvar insumo');
    } finally {
      setSavingInsumo(false);
    }
  };

  const handleDelete = async (insumo) => {
    if (!window.confirm('Excluir insumo?')) return;
    try {
      await api.delete(`/confereai/insumos/${insumo.id}`);
      toast.success('Insumo excluído');
      fetchInsumos();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao excluir insumo');
    }
  };

  const handleToggleOcultar = async (insumo) => {
    try {
      await api.put(`/confereai/insumos/${insumo.id}`, { oculto_cmv: !insumo.oculto_cmv });
      fetchInsumos();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao atualizar insumo');
    }
  };

  // ===================== CATEGORIA MODAL (CRUD) =====================
  const openCategories = () => {
    setCatNewName('');
    setEditingCatId(null);
    setEditingCatName('');
    setCatModalOpen(true);
  };

  const createCategory = async () => {
    const nome = catNewName.trim();
    if (!nome) return toast.error('Informe o nome da categoria');
    try {
      setCatLoading(true);
      await api.post('/confereai/categorias', { nome });
      toast.success('Categoria criada');
      setCatNewName('');
      await fetchCategorias();
    } catch (e) {
      console.error(e);
      toast.error('Erro ao criar categoria');
    } finally {
      setCatLoading(false);
    }
  };

  const startEditCategory = (cat) => {
    setEditingCatId(cat.id);
    setEditingCatName(cat.nome);
  };

  const saveEditCategory = async () => {
    const nome = editingCatName.trim();
    if (!nome) return toast.error('Informe o nome da categoria');
    try {
      setCatLoading(true);
      await api.put(`/confereai/categorias/${editingCatId}`, { nome });
      toast.success('Categoria atualizada');
      setEditingCatId(null);
      setEditingCatName('');
      await fetchCategorias();
    } catch (e) {
      console.error(e);
      toast.error('Erro ao atualizar categoria');
    } finally {
      setCatLoading(false);
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Excluir categoria?')) return;
    try {
      setCatLoading(true);
      await api.delete(`/confereai/categorias/${id}`);
      toast.success('Categoria excluída');
      await fetchCategorias();
      if (String(categoriaId) === String(id)) setCategoriaId('');
    } catch (e) {
      console.error(e);
      toast.error('Erro ao excluir categoria');
    } finally {
      setCatLoading(false);
    }
  };

  // Para exibir custo na grid (custo_medio OU custo_inicial)
  const rowsWithCost = useMemo(() => {
    return rows.map(r => ({
      ...r,
      _custoParaExibir: r.custo_medio ?? r.custo_inicial ?? null
    }));
  }, [rows]);

  // Mapa de categorias para fallback do nome na grid
  const catById = useMemo(
    () => new Map(categories.map(c => [String(c.id), c.nome])),
    [categories]
  );

  // ========== UI ==========

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          py: 1,
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 2
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #90caf9 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: 0.3
          }}
        >
          Insumos
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<CategoryIcon />} onClick={openCategories}>
            Gerenciar Categorias
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenModal(null)}>
            Novo Item
          </Button>
        </Stack>
      </Box>

      {/* KPIs */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={4}>
          <KpiCard icon={Inventory2} label="Total de Itens" value={kpis.total} />
        </Grid>
        <Grid item xs={12} md={4}>
          <KpiCard icon={VisibilityOff} label="Ocultados do CMV" value={kpis.ocultados} />
        </Grid>
        <Grid item xs={12} md={4}>
          <KpiCard icon={FolderOpen} label="Total de Categorias" value={kpis.categorias} />
        </Grid>
      </Grid>

      {/* Filtros */}
      <Paper
        component={motion.div}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.9), rgba(250,250,250,0.7))',
          backdropFilter: 'blur(8px)',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              label="Buscar"
              fullWidth
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Categoria</InputLabel>
              <Select
                value={categoriaId}
                label="Categoria"
                onChange={(e) => { setCategoriaId(e.target.value); setPage(1); }}
              >
                <MenuItem value=""><em>Todas</em></MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>{cat.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabela */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          background: '#fff'
        }}
      >
        <Table
          size="small"
          sx={{
            '& thead th': { bgcolor: 'grey.50', fontWeight: 600 },
            '& th, & td': { px: 2 },
            '& th:first-of-type, & td:first-of-type': { pl: 3 },
            '& th:last-of-type,  & td:last-of-type':  { pr: 3 },
            '& thead th:first-of-type': { borderTopLeftRadius: 12 },
            '& thead th:last-of-type':  { borderTopRightRadius: 12 },
            '& tbody tr:hover': { bgcolor: 'action.hover' },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell width={64}>Ocultar</TableCell>
              <TableCell>Item</TableCell>
              <TableCell width={150} align="right">Estoque Mínimo</TableCell>
              <TableCell width={140}>Medida</TableCell>
              <TableCell width={160}>Custo Médio</TableCell>
              <TableCell>Categoria</TableCell>
              <TableCell>Especificação</TableCell>
              <TableCell width={120} align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <AnimatePresence initial={false}>
              {loading ? (
                [...Array(6)].map((_, i) => (
                  <TableRow key={`skeleton-${i}`}>
                    <TableCell><Skeleton variant="rectangular" height={24} /></TableCell>
                    <TableCell><Skeleton height={20} /></TableCell>
                    <TableCell align="right"><Skeleton height={20} width="50%" /></TableCell> {/* NOVO */}
                    <TableCell><Skeleton height={20} width="60%" /></TableCell>
                    <TableCell><Skeleton height={20} width="70%" /></TableCell>
                    <TableCell><Skeleton height={20} width="50%" /></TableCell>
                    <TableCell><Skeleton height={20} /></TableCell>
                    <TableCell align="right"><Skeleton height={24} width={80} /></TableCell>
                  </TableRow>
                ))
              ) : rowsWithCost.length > 0 ? (
                rowsWithCost.map((row) => (
                  <TableRow
                    key={row.id}
                    component={motion.tr}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.15 }}
                    hover
                  >
                    <TableCell>
                      <Checkbox checked={!!row.oculto_cmv} onChange={() => handleToggleOcultar(row)} />
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600}>{row.nome}</Typography>
                    </TableCell>
                    <TableCell align="right">{row.estoque_minimo ?? 0}</TableCell> {/* NOVO */}
                    <TableCell>{row.unidade_medida || row.medida || '-'}</TableCell>
                    <TableCell>{formatBRL(row._custoParaExibir)}</TableCell>
                    <TableCell>
                      {row.categoria?.nome
                        ?? row.categoria_nome
                        ?? catById.get(String(row.categoria_id))
                        ?? '-'}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{row.especificacao || '-'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton onClick={() => handleOpenModal(row)} size="small" aria-label="Editar insumo">
                          <Edit fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton onClick={() => handleDelete(row)} size="small" aria-label="Excluir insumo">
                          <Delete fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">Nenhum insumo encontrado</Typography>
                  </TableCell>
                </TableRow>
              )}
            </AnimatePresence>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box mt={2} display="flex" justifyContent="center" alignItems="center" gap={1}>
          <Button disabled={page === 1} onClick={() => setPage(page - 1)}>Anterior</Button>
          <Box px={2}>{page} / {totalPages}</Box>
          <Button disabled={page === totalPages} onClick={() => setPage(page + 1)}>Próxima</Button>
        </Box>
      )}

      {/* ===== Insumo Dialog (modern/animated) ===== */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        fullWidth
        maxWidth="sm"
        TransitionComponent={TransitionUp}
        PaperProps={{
          component: motion.div,
          initial: { opacity: 0, y: 16, scale: 0.98 },
          animate: { opacity: 1, y: 0, scale: 1 },
          exit: { opacity: 0, y: 16, scale: 0.98 },
          transition: { duration: 0.2 },
          sx: { borderRadius: 3, overflow: 'hidden' }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            background: 'linear-gradient(90deg, rgba(25,118,210,0.05), rgba(25,118,210,0))'
          }}
        >
          <Add fontSize="small" /> {current ? 'Editar' : 'Novo'} Item
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Nome"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                fullWidth
                autoFocus
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Unidade de Medida</InputLabel>
                <Select
                  label="Unidade de Medida"
                  value={form.unidade_medida}
                  onChange={(e) => setForm({ ...form, unidade_medida: e.target.value })}
                >
                  {MEDIDAS.map((m) => (
                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Custo Inicial (R$)"
                type="number"
                inputProps={{ step: '0.01', min: '0' }}
                value={form.custo_inicial}
                onChange={(e) => setForm({ ...form, custo_inicial: e.target.value })}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Estoque Mínimo"
                type="number"
                inputProps={{ step: '1', min: '0' }}
                value={form.estoque_minimo}
                onChange={(e) => setForm({ ...form, estoque_minimo: e.target.value })}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Categoria</InputLabel>
                <Select
                  value={form.categoria_id}
                  label="Categoria"
                  onChange={(e) => setForm({ ...form, categoria_id: e.target.value })}
                >
                  <MenuItem value=""><em>Sem categoria</em></MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>{cat.nome}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Especificação"
                value={form.especificacao}
                onChange={(e) => setForm({ ...form, especificacao: e.target.value })}
                fullWidth
                multiline
                minRows={2}
              />
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Checkbox
                  checked={form.oculto_cmv}
                  onChange={(e) => setForm({ ...form, oculto_cmv: e.target.checked })}
                />
                <Typography>Ocultar do CMV</Typography>
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button startIcon={<Close />} onClick={() => setOpenModal(false)}>Cancelar</Button>
          <Button
            startIcon={savingInsumo ? <CircularProgress size={18} /> : <Save />}
            variant="contained"
            onClick={handleSave}
            disabled={savingInsumo}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Category Manager Dialog ===== */}
      <Dialog
        open={catModalOpen}
        onClose={() => setCatModalOpen(false)}
        fullWidth
        maxWidth="sm"
        TransitionComponent={TransitionUp}
        PaperProps={{
          component: motion.div,
          initial: { opacity: 0, y: 20 },
          animate: { opacity: 1, y: 0 },
          exit: { opacity: 0, y: 20 },
          transition: { duration: 0.2 },
          sx: { borderRadius: 3, overflow: 'hidden' }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            background: 'linear-gradient(90deg, rgba(25,118,210,0.05), rgba(25,118,210,0))'
          }}
        >
          <CategoryIcon fontSize="small" /> Gerenciar Categorias
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch">
            <TextField
              label="Nova categoria"
              value={catNewName}
              onChange={(e) => setCatNewName(e.target.value)}
              fullWidth
            />
            <Button onClick={createCategory} variant="contained" disabled={catLoading}>
              {catLoading ? <CircularProgress size={18} /> : 'Adicionar'}
            </Button>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <AnimatePresence mode="popLayout">
            {categories.map((cat) => (
              <motion.div
                key={cat.id}
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15 }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5, mb: 1,
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    borderRadius: 2
                  }}
                >
                  <CategoryIcon fontSize="small" color="primary" />
                  <Typography sx={{ flex: 1, fontWeight: 600 }}>{cat.nome}</Typography>

                  {editingCatId === cat.id ? (
                    <>
                      <TextField
                        size="small"
                        value={editingCatName}
                        onChange={(e) => setEditingCatName(e.target.value)}
                        sx={{ mr: 1, width: { xs: '100%', sm: 220 } }}
                      />
                      <Button size="small" onClick={saveEditCategory} variant="contained" disabled={catLoading}>Salvar</Button>
                      <Button size="small" onClick={() => { setEditingCatId(null); setEditingCatName(''); }}>Cancelar</Button>
                    </>
                  ) : (
                    <>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => startEditCategory(cat)} aria-label="Editar categoria">
                          <Edit fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => deleteCategory(cat.id)} aria-label="Excluir categoria">
                          <Delete fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Paper>
              </motion.div>
            ))}
          </AnimatePresence>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button onClick={() => setCatModalOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Insumos;
